import React, { useState } from 'react';
import {
    Typography,
    Checkbox,
    Box,
    Card,
    Chip,
    Grid,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VideocamIcon from '@mui/icons-material/Videocam';
import PersonIcon from '@mui/icons-material/Person';
import DevicesIcon from '@mui/icons-material/Devices';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import { ApplicationDetails, CampaignMode } from '../../../../types/Applications';

interface ApplicationSettingsProps {
    applicationDetails: ApplicationDetails;
    onAvailabilityChange: (checked: boolean) => void;
    onLocationChange: (checked: boolean) => void;
    onCampaignModeChange?: (mode: CampaignMode) => void;
}

const ApplicationSettings: React.FC<ApplicationSettingsProps> = ({
    applicationDetails,
    onAvailabilityChange,
    onLocationChange,
    onCampaignModeChange
}) => {
    const [expanded, setExpanded] = useState<boolean>(true);

    const handleAccordionChange = () => {
        setExpanded(!expanded);
    };
    return (
        <Accordion
            expanded={expanded}
            onChange={handleAccordionChange}
            sx={{
                mb: 3,
                borderRadius: 2,
                overflow: 'hidden',
                '&:before': {
                    display: 'none',
                },
                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)'
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="application-settings-content"
                id="application-settings-header"
                sx={{
                    backgroundColor: 'background.paper',
                    borderBottom: expanded ? '1px solid' : 'none',
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <SettingsIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        Application Settings
                    </Typography>

                    {!expanded && (
                        <Box sx={{ display: 'flex', ml: 'auto', alignItems: 'center', gap: 1 }}>
                            <Chip
                                size="small"
                                label={applicationDetails.campaign_mode ?
                                    applicationDetails.campaign_mode.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) :
                                    'No mode selected'}
                                color="primary"
                                variant="outlined"
                            />
                            {applicationDetails.include_availability && (
                                <Tooltip title="Availability required">
                                    <Chip
                                        icon={<AccessTimeIcon style={{ fontSize: '16px' }} />}
                                        size="small"
                                        label="Availability"
                                        color="default"
                                        variant="outlined"
                                    />
                                </Tooltip>
                            )}
                            {applicationDetails.include_location && (
                                <Tooltip title="Location required">
                                    <Chip
                                        icon={<LocationOnIcon style={{ fontSize: '16px' }} />}
                                        size="small"
                                        label="Location"
                                        color="default"
                                        variant="outlined"
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3, pb: 2, backgroundColor: 'background.paper' }}>

                {/* Campaign Mode Selection */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                        Campaign Format
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item>
                            <Card
                                raised={applicationDetails.campaign_mode === CampaignMode.InPerson}
                                onClick={() => onCampaignModeChange && onCampaignModeChange(CampaignMode.InPerson)}
                                sx={{
                                    p: 2,
                                    width: '120px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    borderColor: applicationDetails.campaign_mode === CampaignMode.InPerson ? 'primary.main' : 'transparent',
                                    borderWidth: 2,
                                    borderStyle: 'solid',
                                    bgcolor: applicationDetails.campaign_mode === CampaignMode.InPerson ? 'primary.50' : 'background.paper',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        bgcolor: 'primary.50'
                                    }
                                }}
                            >
                                <PersonIcon sx={{ fontSize: 40, mb: 1, color: applicationDetails.campaign_mode === CampaignMode.InPerson ? 'primary.main' : 'text.secondary' }} />
                                <Typography variant="body2" fontWeight={500}>
                                    In Person
                                </Typography>
                            </Card>
                        </Grid>

                        <Grid item>
                            <Card
                                raised={applicationDetails.campaign_mode === CampaignMode.Hybrid}
                                onClick={() => onCampaignModeChange && onCampaignModeChange(CampaignMode.Hybrid)}
                                sx={{
                                    p: 2,
                                    width: '120px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    borderColor: applicationDetails.campaign_mode === CampaignMode.Hybrid ? 'primary.main' : 'transparent',
                                    borderWidth: 2,
                                    borderStyle: 'solid',
                                    bgcolor: applicationDetails.campaign_mode === CampaignMode.Hybrid ? 'primary.50' : 'background.paper',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        bgcolor: 'primary.50'
                                    }
                                }}
                            >
                                <DevicesIcon sx={{ fontSize: 40, mb: 1, color: applicationDetails.campaign_mode === CampaignMode.Hybrid ? 'primary.main' : 'text.secondary' }} />
                                <Typography variant="body2" fontWeight={500}>
                                    Hybrid
                                </Typography>
                            </Card>
                        </Grid>

                        <Grid item>
                            <Card
                                raised={applicationDetails.campaign_mode === CampaignMode.Virtual}
                                onClick={() => onCampaignModeChange && onCampaignModeChange(CampaignMode.Virtual)}
                                sx={{
                                    p: 2,
                                    width: '120px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    borderColor: applicationDetails.campaign_mode === CampaignMode.Virtual ? 'primary.main' : 'transparent',
                                    borderWidth: 2,
                                    borderStyle: 'solid',
                                    bgcolor: applicationDetails.campaign_mode === CampaignMode.Virtual ? 'primary.50' : 'background.paper',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        bgcolor: 'primary.50'
                                    }
                                }}
                            >
                                <VideocamIcon sx={{ fontSize: 40, mb: 1, color: applicationDetails.campaign_mode === 'virtual' ? 'primary.main' : 'text.secondary' }} />
                                <Typography variant="body2" fontWeight={500}>
                                    Virtual
                                </Typography>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* Required Information Options */}
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                    Required Information
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card
                            onClick={() => onAvailabilityChange(!applicationDetails.include_availability)}
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                borderColor: applicationDetails.include_availability ? 'primary.main' : 'divider',
                                borderWidth: 1,
                                borderStyle: 'solid',
                                bgcolor: applicationDetails.include_availability ? 'primary.50' : 'background.paper',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: 'primary.light'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <AccessTimeIcon sx={{
                                        mr: 1,
                                        color: applicationDetails.include_availability ? 'primary.main' : 'text.secondary'
                                    }} />
                                    <Typography variant="body1" fontWeight={500}>
                                        Availability
                                    </Typography>
                                </Box>
                                <Checkbox
                                    checked={applicationDetails.include_availability}
                                    onChange={(e) => onAvailabilityChange(e.target.checked)}
                                    color="primary"
                                    size="small"
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Allows mentors and mentees to select available times to ensure meeting overlap
                            </Typography>
                            <Tooltip title="Critical for mentor-mentee matching">
                                <Chip
                                    label="Critical Match"
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                />
                            </Tooltip>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <Card
                            onClick={() => onLocationChange(!applicationDetails.include_location)}
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                borderColor: applicationDetails.include_location ? 'primary.main' : 'divider',
                                borderWidth: 1,
                                borderStyle: 'solid',
                                bgcolor: applicationDetails.include_location ? 'primary.50' : 'background.paper',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: 'primary.light'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LocationOnIcon sx={{
                                        mr: 1,
                                        color: applicationDetails.include_location ? 'primary.main' : 'text.secondary'
                                    }} />
                                    <Typography variant="body1" fontWeight={500}>
                                        Location
                                    </Typography>
                                </Box>
                                <Checkbox
                                    checked={applicationDetails.include_location}
                                    onChange={(e) => onLocationChange(e.target.checked)}
                                    color="primary"
                                    size="small"
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Allows mentees and mentors to specify locations and travel distances for in-person meetings
                            </Typography>
                            <Tooltip title="Critical for mentor-mentee matching">
                                <Chip
                                    label="Critical Match"
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                />
                            </Tooltip>
                        </Card>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};

export default ApplicationSettings;